import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';

import { AuthService } from '../../services/auth.service';
import { FilesService } from '../../services/files.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { environment } from 'src/environments/environment';

interface StagingUpload {
  id: string;
  uploadSessionId: string;
  status: string;
  fileCount: number;
  failedFiles: string[];
  scanStatus?: string;
  scanError?: string;
  infectedCount?: number;
  studies: StudyRecord[];
  gsUriBase: string;
  datasetId?: string;
  dicomStoreId?: string;
  importedAt?: any;
  createdAt?: any;
}

interface StudyRecord {
  StudyInstanceUID?: string;
  PatientName?: string;
  StudyDate?: string;
  Modality?: string;
  StudyDescription?: string;
  NumberOfStudyRelatedSeries?: number;
}

interface ClioMatter {
  id: number;
  display_number: string;
  description: string;
  client?: { name?: string };
}

@Component({
  selector: 'app-imaging-case-detail',
  templateUrl: './imaging-case-detail.component.html',
  styleUrls: ['./imaging-case-detail.component.scss'],
})
export class ImagingCaseDetailComponent implements OnInit, OnDestroy {
  caseId: string;
  imagingCase: any = null;
  uploads: StagingUpload[] = [];
  selectedUploadIds = new Set<string>();
  loading = true;
  submitting = false;

  // Link-to-Clio state
  showLinkPanel = false;
  mattersLoading = false;
  matters: ClioMatter[] = [];
  matterFilter = '';
  selectedMatter: ClioMatter | null = null;
  linking = false;

  private caseUnsub: () => void;
  private uploadsUnsub: () => void;
  private hasInitializedSelection = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth_$: AuthService,
    private files_$: FilesService,
    private ui_$: UIMessagingService,
    private http: HttpClient,
  ) {}

  ngOnInit() {
    this.auth_$.preventRedirections = true;
    this.caseId = this.route.snapshot.paramMap.get('id');
    this.subscribeToCase();
    this.subscribeToUploads();
  }

  ngOnDestroy() {
    this.caseUnsub?.();
    this.uploadsUnsub?.();
  }

  private subscribeToCase() {
    this.caseUnsub = firebase.firestore()
      .collection('imagingCases')
      .doc(this.caseId)
      .onSnapshot(snap => {
        this.imagingCase = snap.exists ? { id: snap.id, ...snap.data() } : null;
        this.loading = false;
      });
  }

  private subscribeToUploads() {
    this.uploadsUnsub = firebase.firestore()
      .collection('dicomStagingUploads')
      .where('imagingCaseId', '==', this.caseId)
      .orderBy('createdAt', 'desc')
      .onSnapshot(snap => {
        this.uploads = snap.docs.map(d => ({ id: d.id, ...d.data() } as StagingUpload));
        if (!this.hasInitializedSelection) {
          this.selectAllAwaiting();
          this.hasInitializedSelection = true;
          return;
        }
        this.syncSelectionWithUploads();
      });
  }

  get awaitingUploads(): StagingUpload[] {
    return this.uploads.filter(u => u.status === 'awaiting_submit');
  }

  get selectedAwaitingUploads(): StagingUpload[] {
    return this.awaitingUploads.filter(u => this.selectedUploadIds.has(u.id));
  }

  get selectedCount(): number {
    return this.selectedAwaitingUploads.length;
  }

  get canSubmitSelected(): boolean {
    return !this.submitting && this.selectedCount > 0;
  }

  get canLink(): boolean {
    return this.imagingCase?.status === 'processed';
  }

  get filteredMatters(): ClioMatter[] {
    if (!this.matterFilter.trim()) return this.matters;
    const q = this.matterFilter.toLowerCase();
    return this.matters.filter(m =>
      m.display_number?.toLowerCase().includes(q) ||
      m.description?.toLowerCase().includes(q) ||
      m.client?.name?.toLowerCase().includes(q),
    );
  }

  statusLabel(status: string): string {
    const labels: Record<string, string> = {
      awaiting_submit: 'Awaiting Submit',
      staged: 'Queued for Import',
      scanning: 'Scanning for viruses…',
      quarantined: 'Quarantined',
      processing: 'Importing…',
      imported: 'Imported',
      error: 'Error',
    };
    return labels[status] || status;
  }

  /** What the chip shows. A submitted upload is scanned before it is imported, so 'staged' without a clean scan reads as scanning. */
  displayKey(upload: StagingUpload): string {
    if (upload.status === 'staged' && upload.scanStatus !== 'clean') return 'scanning';
    return upload.status;
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      awaiting_submit: 'status-pending',
      staged: 'status-queued',
      scanning: 'status-processing',
      quarantined: 'status-failed',
      processing: 'status-processing',
      imported: 'status-processed',
      error: 'status-failed',
    };
    return map[status] || '';
  }

  statusIcon(status: string): string {
    const icons: Record<string, string> = {
      awaiting_submit: 'hourglass_empty',
      staged: 'schedule',
      scanning: 'sync',
      quarantined: 'gpp_bad',
      processing: 'sync',
      imported: 'check_circle',
      error: 'error',
    };
    return icons[status] || 'help';
  }

  caseStatusClass(status: string): string {
    const map: Record<string, string> = {
      pending: 'status-pending',
      queued: 'status-queued',
      processing: 'status-processing',
      processed: 'status-processed',
      linked: 'status-linked',
      failed: 'status-failed',
    };
    return map[status] || '';
  }

  viewerUrl(upload: StagingUpload, studyUid: string): string | null {
    if (!upload.datasetId || !upload.dicomStoreId || !studyUid) return null;
    const project = environment.config.projectId;
    const location = environment.config.location;
    const wadoRsRoot = encodeURIComponent(
      `https://healthcare.googleapis.com/v1/projects/${project}/locations/${location}` +
      `/datasets/${upload.datasetId}/dicomStores/${upload.dicomStoreId}/dicomWeb`
    );
    return `${environment.config.ohifViewerURL}?StudyInstanceUIDs=${studyUid}&wadoRsRoot=${wadoRsRoot}`;
  }

  isAwaiting(upload: StagingUpload): boolean {
    return upload.status === 'awaiting_submit';
  }

  isSelected(uploadId: string): boolean {
    return this.selectedUploadIds.has(uploadId);
  }

  toggleSelected(uploadId: string, checked: boolean) {
    if (checked) {
      this.selectedUploadIds.add(uploadId);
      return;
    }
    this.selectedUploadIds.delete(uploadId);
  }

  selectAllAwaiting() {
    this.selectedUploadIds.clear();
    for (const upload of this.awaitingUploads) {
      this.selectedUploadIds.add(upload.id);
    }
  }

  clearSelection() {
    this.selectedUploadIds.clear();
  }

  private syncSelectionWithUploads() {
    const awaitingIds = new Set(this.awaitingUploads.map(u => u.id));
    for (const id of Array.from(this.selectedUploadIds)) {
      if (!awaitingIds.has(id)) {
        this.selectedUploadIds.delete(id);
      }
    }
  }

  async submitForProcessing() {
    if (!this.canSubmitSelected) return;
    this.submitting = true;
    try {
      const ids = this.selectedAwaitingUploads.map(u => u.id);
      await this.files_$.submitForProcessing(ids, this.caseId);
      this.ui_$.toastMessage(`Submitted ${ids.length} session(s) for processing — import will begin within 5 minutes.`, 'OK');
    } catch (err: any) {
      this.ui_$.toastMessage('Failed to submit: ' + err.message, 'OK');
    } finally {
      this.submitting = false;
    }
  }

  // ---- Patient upload links ----------------------------------------------------------------------------------------------

  async openLinkPanel() {
    this.showLinkPanel = true;
    this.selectedMatter = null;
    this.matterFilter = '';
    if (this.matters.length > 0) return; // already loaded

    this.mattersLoading = true;
    try {
      const token = await firebase.auth().currentUser.getIdToken();
      const uid = firebase.auth().currentUser.uid;
      const userdocIdSnap = await firebase.firestore()
        .collection('users').where('uid', '==', uid).limit(1).get();
      const userdocid = userdocIdSnap.empty ? uid : userdocIdSnap.docs[0].id;

      const url = `${environment.constants.cloudfunctionsURL}clio-securedGetMatters`;
      const res: any = await this.http.post(
        url,
        { userdocid, uid },
        { headers: new HttpHeaders().set('Authorization', 'Bearer ' + token) },
      ).toPromise();

      this.matters = (res?.data || []) as ClioMatter[];
    } catch (err: any) {
      this.ui_$.toastMessage('Could not load Clio matters: ' + (err.message || 'unknown error'), 'OK');
      this.showLinkPanel = false;
    } finally {
      this.mattersLoading = false;
    }
  }

  selectMatter(matter: ClioMatter) {
    this.selectedMatter = matter;
  }

  async confirmLink() {
    if (!this.selectedMatter || this.linking) return;
    this.linking = true;
    try {
      const m = this.selectedMatter;
      const result = await this.files_$.linkToClioMatter(
        this.caseId,
        m.id,
        m.description || m.display_number,
        m.display_number,
      );
      this.ui_$.toastMessage(
        `Linked to "${m.display_number}" — matter profile created as "${result.patientCaseName}".`,
        'OK',
      );
      this.showLinkPanel = false;
    } catch (err: any) {
      this.ui_$.toastMessage('Failed to link: ' + err.message, 'OK');
    } finally {
      this.linking = false;
    }
  }

  cancelLink() {
    this.showLinkPanel = false;
    this.selectedMatter = null;
  }

  goBack() {
    this.router.navigate(['/imaging_cases']);
  }

  formatDate(ts: any): string {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
}
